/**
 * /suprimentos/movimentos — historico imutavel + criar movimento manual.
 */
import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Carregando, Vazio } from "./_shared/Estados";
import { FiAlertCircle, FiClock, FiPlus, FiRefreshCw } from "react-icons/fi";

import EntityHeader from "../../components/EntityHeader";
import SlideOverPanel from "../../components/SlideOverPanel";
import { useAuth } from "../../context/AuthContext";
import useAjustesAguardando, { notificarAjustesMudou } from "../../hooks/useAjustesAguardando";
import { criarMovimento, listarDepositos, listarMovimentos, listarProdutos } from "../../services/estoque";
import { linhasDe, listarMotivosAjuste } from "../../services/suprimentos";
import AjustesAprovacaoTab from "./AjustesAprovacaoTab";
import { AbaBtn, AbasWrap } from "./Sobras/styles";

import {
  fmtData,
  fmtMoeda,
  fmtQtd,
  ORIGEM_MOV_LABEL,
  TIPO_MOV_LABEL,
  TIPO_MOV_TONE,
} from "./helpers";
import {
  Card,
  ErrorMsg,
  Field,
  Form,
  FormFooter,
  FormGrid,
  InlineHint,
  InfoMsg,
  Input,
  Pill,
  PrimaryButton,
  SecondaryButton,
  Select,
  Table,
  TableWrapper,
  Textarea,
  TruncCell,
} from "./styles";

const ABAS = [
  { id: "razao", label: "Razão" },
  { id: "aprovacao", label: "Aguardando aprovação" },
];

function Movimentos() {
  const { request, isMaster, hasAllPermissions } = useAuth();
  // Bloco 4 (12/09/2026): a fila de ajustes mora aqui, como aba. A URL
  // (?aba=aprovacao) e o que o sino e o hub linkam.
  const [params, setParams] = useSearchParams();
  const aba = params.get("aba") === "aprovacao" ? "aprovacao" : "razao";
  const trocarAba = (id) => setParams(id === "razao" ? {} : { aba: id }, { replace: true });
  const podeAprovar = isMaster || hasAllPermissions(["estoque:aprovar"]);
  const aguardando = useAjustesAguardando({ enabled: podeAprovar });
  const [movimentos, setMovimentos] = useState([]);
  const [produtos, setProdutos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState(null);
  // `?produto=<id>` chega do menu "Movimentar" do Cadastro de Suprimentos.
  const [filtros, setFiltros] = useState({ produto: params.get("produto") || "", tipo: "", origem: "" });
  const [novo, setNovo] = useState(null);
  const [depositos, setDepositos] = useState([]);
  const [motivos, setMotivos] = useState([]);
  const [pendente, setPendente] = useState(null);

  const podeRegistrar = isMaster || hasAllPermissions(["estoque:ajustar"]);

  useEffect(() => {
    listarDepositos(request, { ativo: true }).then((p) => setDepositos(linhasDe(p))).catch(() => setDepositos([]));
    listarMotivosAjuste(request).then((p) => setMotivos(linhasDe(p).filter((m) => m.ativo))).catch(() => setMotivos([]));
  }, [request]);

  useEffect(() => {
    listarProdutos(request, { pageSize: 500, ativo: true })
      .then((p) => {
        const items = Array.isArray(p) ? p : p?.results || [];
        setProdutos(items);
      })
      .catch(() => setProdutos([]));
  }, [request]);

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const params = { pageSize: 200 };
      if (filtros.produto) params.produto = filtros.produto;
      if (filtros.tipo) params.tipo = filtros.tipo;
      if (filtros.origem) params.origem = filtros.origem;
      const payload = await listarMovimentos(request, params);
      const items = Array.isArray(payload) ? payload : payload?.results || [];
      setMovimentos(items);
      setErro(null);
    } catch (err) {
      setErro(err?.message || "Erro ao carregar movimentos.");
    } finally {
      setLoading(false);
    }
  }, [request, filtros.produto, filtros.tipo, filtros.origem]);

  useEffect(() => { carregar(); }, [carregar]);

  const handleSalvar = async (e) => {
    e.preventDefault();
    if (!novo.produto || !novo.tipo || !novo.quantidade) {
      setErro("Produto, tipo e quantidade são obrigatórios.");
      return;
    }
    // D30: ajuste sem motivo não existe — é o que separa correção de sumiço.
    if (novo.tipo === "AJUSTE" && !novo.motivo_ajuste) {
      setErro("Informe o motivo do ajuste.");
      return;
    }
    setErro(null);
    try {
      const resp = await criarMovimento(request, {
        produto: novo.produto,
        tipo: novo.tipo,
        quantidade: novo.quantidade,
        custo_unitario: novo.custo_unitario || "0",
        observacao: novo.observacao || "",
        deposito: novo.deposito || null,
        motivo_ajuste: novo.tipo === "AJUSTE" ? novo.motivo_ajuste : null,
      });
      setNovo(null);
      // 202: acima do limite da unidade — vai para a aba de aprovação, o
      // saldo ainda não mudou. Dizer isso aqui evita o "lancei e não apareceu".
      setPendente(resp?.aguardando_aprovacao ? resp.ajuste_pendente : null);
      if (resp?.aguardando_aprovacao) notificarAjustesMudou();
      await carregar();
    } catch (err) {
      setErro(err?.payload?.detalhe || err?.payload?.motivo_ajuste || err?.message || "Erro ao registrar movimento.");
    }
  };

  return (
    <>
      <EntityHeader
        title="Movimentos de estoque"
        subtitle={aba === "aprovacao" ? "Ajustes acima do limite da unidade esperando aprovação." : `${movimentos.length} no resultado`}
        primaryAction={
          podeRegistrar
            ? {
                label: "Novo movimento",
                icon: <FiPlus size={14} />,
                onClick: () => setNovo({
                  produto: "", tipo: "ENTRADA", quantidade: "",
                  custo_unitario: "", observacao: "", deposito: "", motivo_ajuste: "",
                }),
              }
            : undefined
        }
        actions={[
          { label: "Atualizar", icon: <FiRefreshCw size={14} />, onClick: carregar },
        ]}
      />

      <AbasWrap role="tablist" aria-label="Movimentos">
        {ABAS.map((a) => (
          <AbaBtn key={a.id} type="button" role="tab" $on={aba === a.id} aria-selected={aba === a.id} onClick={() => trocarAba(a.id)}>
            {a.label}{a.id === "aprovacao" && aguardando > 0 ? ` (${aguardando})` : ""}
          </AbaBtn>
        ))}
      </AbasWrap>

      {aba === "aprovacao" && (
        <Card>
          <AjustesAprovacaoTab onMudou={carregar} />
        </Card>
      )}

      {aba === "razao" && (
      <Card>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <Select
            value={filtros.produto}
            onChange={(e) => setFiltros((p) => ({ ...p, produto: e.target.value }))}
            style={{ minWidth: 220 }}
          >
            <option value="">Todos os produtos</option>
            {produtos.map((p) => (
              <option key={p.id} value={p.id}>{p.codigo} — {p.nome}</option>
            ))}
          </Select>
          <Select
            value={filtros.tipo}
            onChange={(e) => setFiltros((p) => ({ ...p, tipo: e.target.value }))}
          >
            <option value="">Todos os tipos</option>
            <option value="ENTRADA">Entrada</option>
            <option value="SAIDA">Saída</option>
            <option value="AJUSTE">Ajuste</option>
          </Select>
          <Select
            value={filtros.origem}
            onChange={(e) => setFiltros((p) => ({ ...p, origem: e.target.value }))}
          >
            <option value="">Todas as origens</option>
            <option value="MANUAL">Manual</option>
            <option value="OC">Ordem de Compra</option>
            <option value="PRODUCAO">Produção</option>
            <option value="SOBRA">Sobra</option>
            <option value="ALMOX">Almoxarifado</option>
            <option value="INVENT">Inventário</option>
            <option value="TRANSF">Transferência entre depósitos</option>
          </Select>
        </div>

        {erro && <ErrorMsg><FiAlertCircle size={14} /> {erro}</ErrorMsg>}
        {pendente && (
          <InfoMsg>
            <FiClock size={14} /> Ajuste de {fmtQtd(pendente.quantidade)} em {pendente.produto_codigo} ficou
            aguardando aprovação (valor {fmtMoeda(pendente.valor_estimado)} acima do limite). O saldo só muda
            depois que alguém aprovar na aba{" "}
            <button type="button" onClick={() => trocarAba("aprovacao")} style={{ background: "none", border: 0, padding: 0, color: "inherit", font: "inherit", textDecoration: "underline", cursor: "pointer" }}>
              Aguardando aprovação
            </button>.
          </InfoMsg>
        )}

        {loading ? (
          <Carregando />
        ) : movimentos.length === 0 ? (
          <Vazio titulo="Nenhum movimento encontrado" />
        ) : (
          <TableWrapper>
            <Table>
              <colgroup>
                <col style={{ width: "16%" }} />
                <col style={{ width: "12%" }} />
                <col style={{ width: "26%" }} />
                <col style={{ width: "10%" }} />
                <col style={{ width: "12%" }} />
                <col style={{ width: "12%" }} />
                <col style={{ width: "12%" }} />
              </colgroup>
              <thead>
                <tr>
                  <th>Data</th>
                  <th>Tipo</th>
                  <th>Produto</th>
                  <th>Qtd</th>
                  <th>Custo</th>
                  <th>Origem</th>
                  <th>Por</th>
                </tr>
              </thead>
              <tbody>
                {movimentos.map((m) => (
                  <tr key={m.id}>
                    <td>{fmtData(m.created_at)}</td>
                    <td>
                      <Pill $tone={TIPO_MOV_TONE[m.tipo]}>{TIPO_MOV_LABEL[m.tipo]}</Pill>
                    </td>
                    <td title={`${m.produto_codigo} — ${m.produto_nome}${m.deposito_nome ? ` · ${m.deposito_nome}` : ""}`}>
                      <TruncCell>{m.produto_codigo} — {m.produto_nome}</TruncCell>
                      {m.deposito_nome ? (
                        <div style={{ fontSize: 11, color: "var(--ink-3)" }}><TruncCell>{m.deposito_nome}</TruncCell></div>
                      ) : null}
                    </td>
                    <td>{fmtQtd(m.quantidade)}</td>
                    <td>{fmtMoeda(m.custo_unitario)}</td>
                    <td>
                      <Pill $tone="neutral" style={{ fontSize: "0.68rem" }}>
                        {ORIGEM_MOV_LABEL[m.origem] || m.origem}
                      </Pill>
                    </td>
                    <td title={m.criado_por_nome}><TruncCell>{m.criado_por_nome || "—"}</TruncCell></td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </TableWrapper>
        )}
      </Card>
      )}

      <SlideOverPanel
        open={Boolean(novo)}
        onClose={() => setNovo(null)}
        title="Novo movimento manual"
        subtitle="Ajuste de inventário, entrada/saída pontual"
        width="min(90vw, 440px)"
      >
        {novo && (
          <Form onSubmit={handleSalvar}>
            <Field>
              Produto *
              <Select
                value={novo.produto}
                onChange={(e) => setNovo((p) => ({ ...p, produto: e.target.value }))}
                required
              >
                <option value="">— Selecione —</option>
                {produtos.map((p) => (
                  <option key={p.id} value={p.id}>{p.codigo} — {p.nome}</option>
                ))}
              </Select>
            </Field>
            <FormGrid>
              <Field>
                Tipo *
                <Select
                  value={novo.tipo}
                  onChange={(e) => setNovo((p) => ({ ...p, tipo: e.target.value }))}
                >
                  <option value="ENTRADA">Entrada</option>
                  <option value="SAIDA">Saída</option>
                  <option value="AJUSTE">Ajuste (pode ser negativo)</option>
                </Select>
              </Field>
              <Field>
                Quantidade *
                <Input
                  type="number"
                  step="0.001"
                  value={novo.quantidade}
                  onChange={(e) => setNovo((p) => ({ ...p, quantidade: e.target.value }))}
                  required
                />
              </Field>
            </FormGrid>
            <FormGrid>
              <Field>
                Depósito
                <Select
                  value={novo.deposito}
                  onChange={(e) => setNovo((p) => ({ ...p, deposito: e.target.value }))}
                >
                  <option value="">— Depósito padrão —</option>
                  {depositos.map((d) => <option key={d.id} value={d.id}>{d.nome}</option>)}
                </Select>
              </Field>
              {novo.tipo === "AJUSTE" && (
                <Field>
                  Motivo do ajuste *
                  <Select
                    value={novo.motivo_ajuste}
                    onChange={(e) => setNovo((p) => ({ ...p, motivo_ajuste: e.target.value }))}
                    required
                  >
                    <option value="">— Selecione —</option>
                    {motivos.map((m) => <option key={m.id} value={m.id}>{m.nome}</option>)}
                  </Select>
                  {motivos.length === 0 && (
                    <InlineHint>Cadastre os motivos em Suprimentos › Configuração.</InlineHint>
                  )}
                </Field>
              )}
            </FormGrid>
            <Field>
              Custo unitário (opcional, só faz sentido em ENTRADA)
              <Input
                type="number"
                step="0.0001"
                min="0"
                value={novo.custo_unitario}
                onChange={(e) => setNovo((p) => ({ ...p, custo_unitario: e.target.value }))}
              />
              <InlineHint>Em entrada com custo, custo médio do produto é recalculado.</InlineHint>
            </Field>
            <Field>
              Observação
              <Textarea
                value={novo.observacao}
                onChange={(e) => setNovo((p) => ({ ...p, observacao: e.target.value }))}
                rows={3}
                placeholder="Ex: inventário físico 17/05, perda por dano, etc"
              />
            </Field>
            <FormFooter>
              <SecondaryButton type="button" onClick={() => setNovo(null)}>Cancelar</SecondaryButton>
              <PrimaryButton type="submit">Registrar</PrimaryButton>
            </FormFooter>
          </Form>
        )}
      </SlideOverPanel>
    </>
  );
}

export default Movimentos;
